<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';

import {
  useBrowserStore,
  useItineraryStore,
  usePaxStore,
  usePriceCalendarStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { SessionStorageKey } from '@apps/main/new-desktop/core/models';

import { AppButton, AppTooltip, AppPortableText } from '@libs/ui';

import {
  usePremadeTripStore,
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../stores';
import { PremadeTripType } from '../../models';

import TripOverviewHeaderSummaryButton from './TripOverviewHeaderSummaryButton.vue';
import { getHeaderSummarySegments } from './TripOverviewHeaderMobile.helper';
import type { TripSummaryHeader } from './TripOverviewHeaderMobile.helper';
import PremadeTripHeaderMobile from './PremadeTripHeaderMobile.vue';
import AITripDescription from './AITripDescription.vue';

const { t } = i18n.global;

const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const paxStore = usePaxStore();
const priceCalendarStore = usePriceCalendarStore();
const itineraryStore = useItineraryStore();
const recipeStore = useRecipeStore();
const browserStore = useBrowserStore();
const routerStore = useRouterStore();
const premadeTripStore = usePremadeTripStore();

const tripSummaryHeaderSegments = computed<TripSummaryHeader[]>(() =>
  getHeaderSummarySegments(recipeStore)
);

const isCarTripPlannerUser = browserStore.getSessionStorageItem(
  SessionStorageKey.IsCarTripPlannerUser
);

const isCarTripPlannerButtonDisabled = computed<boolean>(
  () =>
    recipeStore.ferryOnlyRecipe.length === 0 &&
    recipeStore.flightOnlyRecipe.length === 0
);

const monthLabel = computed<string>(() =>
  itineraryStore.userSelectedItinerary === undefined
    ? `${t('planner.flexible_in').toLocaleLowerCase()} ${
        priceCalendarStore.calendarTriggerText
      }`
    : priceCalendarStore.calendarTriggerText
);

const firstDestinationName = computed<string | undefined>(() =>
  tripOverviewPageStore.getFirstDestinationName()
);

const firstDestinationNameSaved = ref<string | undefined>(undefined);

watch(
  () => firstDestinationName.value,
  (newValue) => {
    if (newValue && newValue !== firstDestinationNameSaved.value) {
      firstDestinationNameSaved.value = newValue;
    }
  },
  { immediate: true }
);

const firstDestinationNameDisplay = computed<string>(() => {
  if (
    tripOverviewPageStore.numberOfDestinations === 1 &&
    firstDestinationNameSaved.value
  ) {
    return firstDestinationNameSaved.value;
  }

  return `${tripOverviewPageStore.numberOfDestinations} ${t(
    'planner.destination',
    tripOverviewPageStore.numberOfDestinations
  ).toLowerCase()}`;
});

const buttons = ref<HTMLDivElement | undefined>(undefined);
const { height } = useElementSize(buttons);

function openHomeAirportModal(): void {
  statsigEventsStore.logTripOverviewFlyFromButtonClicked();
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'homeAirport',
  });

  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Airplane,
    'overview-header-actions'
  );
}

function openEditTripModal(actionType: 'editTrip' | 'editStay'): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType,
  });

  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Itinerary,
    'overview-header-actions'
  );
}

function openPaxSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'paxSelector',
  });

  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Pax,
    'overview-header-actions'
  );
}

function openMonthsSelectorModal(): void {
  statsigEventsStore.logTripOverviewHeaderActionClicked({
    actionType: 'monthsSelector',
  });

  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Calendar,
    'overview-header-actions'
  );
}
</script>

<template>
  <div class="trip-overview-header-mobile">
    <PremadeTripHeaderMobile
      v-if="premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @show-full-description="
        tripOverviewModalsStore.openPremadeTripDetailsModal
      "
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderMobile>
    <div v-if="!!isCarTripPlannerUser">
      <AppTooltip
        v-if="!isCarTripPlannerButtonDisabled"
        position="bottom-right"
      >
        <template #trigger>
          <AppButton
            class="car-trip-button"
            type="primary"
            state="disabled"
            size="small"
            :right-icon="{
              size: '18',
              name: 'route',
            }"
            @click="routerStore.navigateToCarTrip()"
          >
            <div class="button-content">
              <span>
                {{ $t('open_route_planner') }}
              </span>
            </div>
          </AppButton>
        </template>
        <template #default>
          {{ $t('planner.car_trip.disabled_button') }}
        </template>
      </AppTooltip>
      <AppButton
        v-else
        class="car-trip-button"
        type="primary"
        state="enabled"
        size="small"
        :right-icon="{
          size: '18',
          name: 'route',
        }"
        @click="routerStore.navigateToCarTrip()"
      >
        <div class="button-content">
          <span>
            {{ $t('open_route_planner') }}
          </span>
        </div>
      </AppButton>
    </div>
    <TripOverviewHeaderSummaryButton
      :calendar-text="monthLabel"
      :number-of-nights="tripOverviewPageStore.numberOfNights"
      :number-of-pax="paxStore.numberOfTravelers"
      :header-segments="tripSummaryHeaderSegments"
      :slot-height="height"
    >
      <div ref="buttons" class="buttons">
        <button
          v-if="tripOverviewPageStore.homeFlightData"
          class="button"
          @click="openHomeAirportModal"
        >
          <div class="button-wrapper">
            <div class="label">
              <span class="title">{{ $t('planner.from') }}</span>
              <span class="description">
                {{ tripOverviewPageStore.homeFlightData.airport.name }}
              </span>
            </div>
          </div>
        </button>
        <button class="button" @click="openEditTripModal('editTrip')">
          <div class="button-wrapper">
            <div class="label">
              <span class="title">{{ $t('planner.where') }}</span>
              <span class="description">
                <span>
                  {{ firstDestinationNameDisplay }}
                </span>
              </span>
            </div>
          </div>
        </button>
        <button class="button" @click="openPaxSelectorModal">
          <div class="button-wrapper">
            <div class="label">
              <span class="title">
                {{ $t('planner.pax.travelers', paxStore.numberOfTravelers) }}
              </span>
              <span class="description">
                {{ paxStore.getPaxesButtonDetailedText }}
              </span>
            </div>
          </div>
        </button>
        <button class="button" @click="openMonthsSelectorModal">
          <div class="button-wrapper">
            <div class="label">
              <span class="title">{{ $t('planner.when') }}</span>
              <span class="description">
                {{ monthLabel }}
              </span>
            </div>
          </div>
        </button>
        <button class="button" @click="openEditTripModal('editStay')">
          <div class="button-wrapper">
            <div class="label">
              <span class="title">{{ $t('planner.trip_length') }}</span>
              <span class="description">
                {{ tripOverviewPageStore.numberOfNights }}
                {{
                  $t(
                    'planner.nights',
                    tripOverviewPageStore.numberOfNights
                  ).toLowerCase()
                }}
              </span>
            </div>
          </div>
        </button>
      </div>
    </TripOverviewHeaderSummaryButton>
  </div>
</template>

<style scoped lang="scss">
.trip-overview-header-mobile {
  .car-trip-button {
    margin: 0 var(--spacing-16) var(--spacing-16);

    @include for-less-or-equal-than-599-px {
      margin: 0 auto var(--spacing-12);
    }
  }

  .buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .button {
      all: unset;

      border: 1px solid var(--color-neutral-30);
      border-radius: var(--border-radius-8);
      padding: var(--spacing-12) var(--spacing-16);

      &:first-child {
        margin-top: var(--spacing-16);
      }

      .button-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .label {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          .title {
            @include font-label-m-bold;
          }

          .description {
            @include font-label-m-light;
          }
        }
      }
    }
  }
}
</style>
